"use client";

/**
 * useStrategies Hook
 *
 * Fetches strategies from the API with filtering and sorting.
 * Integrates with the strategies store for UI state management.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useTenant } from "@/hooks/useTenant";
import { useStrategiesStore } from "@/stores/strategiesStore";
import type {
  PerformanceSnapshotResponse,
  Strategy,
  StrategyFilters,
} from "@/types/api.types";

// Stale time: 30 seconds
const STALE_TIME = 30 * 1000;

export interface UseStrategiesOptions {
  /** Override store filters */
  filters?: StrategyFilters;
  /** Whether to enable the query */
  enabled?: boolean;
}

export interface UseStrategiesReturn {
  /** List of strategies from API */
  strategies: Strategy[];
  /** Total count of strategies (for pagination) */
  total: number;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether data is stale and being refetched */
  isFetching: boolean;
}

export function useStrategies(options: UseStrategiesOptions = {}): UseStrategiesReturn {
  const { enabled = true } = options;

  // SFWL-008: strategies are tenant-scoped server-side (x-tenant-id header).
  // The query key must include the active tenant id or switching tenants
  // would serve stale cross-tenant data from the React Query cache.
  const { id: tenantId } = useTenant();

  // Get filter/sort state from store
  const storeFilters = useStrategiesStore((state) => state.filters);
  const sortField = useStrategiesStore((state) => state.sortField);
  const sortOrder = useStrategiesStore((state) => state.sortOrder);
  const setStrategies = useStrategiesStore((state) => state.setStrategies);
  const setError = useStrategiesStore((state) => state.setError);

  // Merge options filters with store filters
  const filters: StrategyFilters = {
    marketView: options.filters?.marketView ?? storeFilters.marketView,
    riskLevel: options.filters?.riskLevel ?? storeFilters.riskLevel,
    assetClass: options.filters?.assetClass ?? storeFilters.assetClass,
    sortBy: options.filters?.sortBy ?? sortField,
    sortOrder: options.filters?.sortOrder ?? sortOrder,
  };

  const query = useQuery({
    queryKey: ["strategies", tenantId ?? null, filters],
    queryFn: async () => {
      const data = await api.strategies.list(filters);
      return data;
    },
    staleTime: STALE_TIME,
    enabled,
  });

  // Sync React Query data into Zustand store.
  // This runs on every render where query.data is available, including
  // when React Query serves cached results without re-running queryFn.
  useEffect(() => {
    if (query.data) {
      const mappedStrategies = query.data.strategies.map((s) => ({
        ...s,
        longDescription: s.longDescription ?? null,
        assetClasses: s.assetClasses ?? [],
        supportedAssets: s.supportedAssets ?? [],
        sharpeRatio: s.sharpeRatio ?? 0,
        maxDrawdown: s.maxDrawdown ?? 0,
        winRate: s.winRate ?? 0,
        avgTradeDurationHours: s.avgTradeDurationHours ?? 0,
        launchDate: s.launchDate ? new Date(s.launchDate) : new Date(),
        userAllocation: null,
      }));
      setStrategies(mappedStrategies);
      setError(null);
    }
  }, [query.data, setStrategies, setError]);

  // Sync error state
  useEffect(() => {
    if (query.error) {
      const message =
        query.error instanceof Error ? query.error.message : "Failed to fetch strategies";
      setError(message);
    }
  }, [query.error, setError]);

  return {
    strategies: query.data?.strategies ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}

/**
 * Hook to fetch a single strategy by ID
 */
export function useStrategy(id: string | null) {
  const setSelectedStrategy = useStrategiesStore((state) => state.setSelectedStrategy);
  // SFWL-008: see useStrategies for why the tenant id is part of the key.
  const { id: tenantId } = useTenant();

  const query = useQuery({
    queryKey: ["strategy", tenantId ?? null, id],
    queryFn: async () => {
      if (!id) throw new Error("Strategy ID is required");

      const strategy = await api.strategies.get(id);

      // Update store with selected strategy
      setSelectedStrategy({
        ...strategy,
        longDescription: strategy.longDescription ?? null,
        assetClasses: strategy.assetClasses ?? [],
        supportedAssets: strategy.supportedAssets ?? [],
        sharpeRatio: strategy.sharpeRatio ?? 0,
        maxDrawdown: strategy.maxDrawdown ?? 0,
        winRate: strategy.winRate ?? 0,
        avgTradeDurationHours: strategy.avgTradeDurationHours ?? 0,
        launchDate: strategy.launchDate ? new Date(strategy.launchDate) : new Date(),
        userAllocation: null,
      });

      return strategy;
    },
    staleTime: STALE_TIME,
    enabled: !!id,
  });

  return {
    strategy: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}

/**
 * Hook to fetch strategy performance snapshots
 */
export function useStrategyPerformance(strategyId: string | null, days = 365) {
  const setPerformanceData = useStrategiesStore((state) => state.setPerformanceData);
  const setLoadingPerformance = useStrategiesStore(
    (state) => state.setLoadingPerformance
  );
  // SFWL-008: see useStrategies for why the tenant id is part of the key.
  const { id: tenantId } = useTenant();

  const query = useQuery({
    queryKey: ["strategy-performance", tenantId ?? null, strategyId, days],
    queryFn: async () => {
      if (!strategyId) throw new Error("Strategy ID is required");

      setLoadingPerformance(true);
      try {
        const response = await api.strategies.getPerformance(strategyId, days);
        const snapshots = response.snapshots.map((s: PerformanceSnapshotResponse) => ({
          ...s,
          timestamp: new Date(s.timestamp),
        }));
        setPerformanceData(snapshots);
        return snapshots;
      } finally {
        setLoadingPerformance(false);
      }
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: !!strategyId,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
